import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Sparkles, Minus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = 'cc_assistant_session_v1';

const getOrCreateSessionId = () => {
  let sid = localStorage.getItem(STORAGE_KEY);
  if (!sid) {
    sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(STORAGE_KEY, sid);
  }
  return sid;
};

const rotateSessionId = () => {
  const sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem(STORAGE_KEY, sid);
  return sid;
};

const SUGGESTED_QUESTIONS = [
  'How does pricing work?',
  'How do I cancel a booking?',
  'What does "Background-checked" mean?',
  'Who brings the cleaning supplies?',
];

const AssistantChat = ({ t }) => {
  const tt = t || ((k, fb) => fb || k);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const sessionIdRef = useRef(getOrCreateSessionId());
  const scrollerRef = useRef(null);
  const panelRef = useRef(null);

  // Hard reset: wipe local state, rotate session id, and (best-effort) delete
  // the chat history on the server so a re-open starts fresh.
  const closeAndClear = () => {
    const oldSid = sessionIdRef.current;
    setOpen(false);
    setMessages([]);
    setInput('');
    sessionIdRef.current = rotateSessionId();
    // Fire-and-forget delete; don't block UI on it
    axios
      .delete(`${API}/assistant/session/${oldSid}`)
      .catch(() => { /* silent — orphan is fine */ });
  };

  // Soft close: keep history so re-opening continues the same conversation.
  const minimize = () => setOpen(false);

  // Close on ESC key — full clear, treating ESC the same as X
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeAndClear();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Close on click outside the panel — full clear, treating outside-click the same as X
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        closeAndClear();
      }
    };
    // delay one tick so the opening click doesn't immediately close it
    const t = setTimeout(() => document.addEventListener('mousedown', onClick), 50);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  // Load history when chat first opens
  useEffect(() => {
    if (!open || messages.length > 0) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/assistant/history`, {
          params: { session_id: sessionIdRef.current },
        });
        if (res.data?.messages?.length) {
          setMessages(res.data.messages);
        }
      } catch (e) {
        // silent
      }
    })();
  }, [open, messages.length]);

  // Auto-scroll
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const sendMessage = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    setInput('');
    setSending(true);

    const userMsg = { id: `u_${Date.now()}`, role: 'user', content: message };
    setMessages((prev) => [...prev, userMsg]);

    // Placeholder assistant message we stream into
    const assistantId = `a_${Date.now()}`;
    setMessages((prev) => [...prev, { id: assistantId, role: 'assistant', content: '' }]);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ session_id: sessionIdRef.current, message }),
      });
      if (!res.ok || !res.body) {
        throw new Error(`HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assistantText = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          try {
            const ev = JSON.parse(payload);
            if (ev.delta) {
              assistantText += ev.delta;
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantId ? { ...m, content: assistantText } : m))
              );
            } else if (ev.error) {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, content: tt('assistant.error', 'Sorry, something went wrong. Please try again.') }
                    : m
                )
              );
            }
          } catch (_e) {
            // ignore malformed frame
          }
        }
      }
    } catch (e) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId
            ? { ...m, content: tt('assistant.error', 'Sorry, something went wrong. Please try again.') }
            : m
        )
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 bg-gradient-to-br from-primary to-emerald-600 hover:scale-105 text-white p-4 rounded-full shadow-2xl transition-transform"
          aria-label={tt('assistant.openLabel', 'Open assistant')}
          data-testid="assistant-bubble"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
            <Sparkles className="w-3 h-3" />
          </span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          ref={panelRef}
          className="fixed bottom-6 right-6 z-40 w-[340px] max-w-[calc(100vw-3rem)] h-[500px] max-h-[calc(100vh-3rem)] bg-white rounded-2xl shadow-2xl border border-border flex flex-col overflow-hidden"
          data-testid="assistant-panel"
          role="dialog"
          aria-label={tt('assistant.title', 'Sparkle Assistant')}
        >
          <div className="bg-gradient-to-r from-primary to-emerald-600 text-white pl-4 pr-2 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="bg-white/20 p-1.5 rounded-full flex-shrink-0">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <h3 className="font-heading font-bold text-sm leading-tight truncate">
                  {tt('assistant.title', 'Sparkle Assistant')}
                </h3>
                <p className="text-[11px] text-white/80 leading-tight truncate">
                  {tt('assistant.subtitle', 'Ask me anything about Match My Cleaner')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={minimize}
                aria-label={tt('assistant.minimizeLabel', 'Minimize')}
                title={tt('assistant.minimizeLabel', 'Minimize (keeps conversation)')}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                data-testid="assistant-minimize"
              >
                <Minus className="w-4 h-4" />
              </button>
              <button
                onClick={closeAndClear}
                aria-label={tt('assistant.closeLabel', 'Close and clear conversation')}
                title={tt('assistant.closeLabel', 'Close & clear conversation (Esc)')}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                data-testid="assistant-close"
              >
                <X className="w-5 h-5" strokeWidth={2.5} />
              </button>
            </div>
          </div>

          <div
            ref={scrollerRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
            data-testid="assistant-messages"
          >
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm text-text-secondary text-center px-4">
                  {tt(
                    'assistant.welcome',
                    "Hi! I'm Sparkle. Ask me anything about how Match My Cleaner works."
                  )}
                </p>
                <div className="space-y-2">
                  {SUGGESTED_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      onClick={() => sendMessage(tt(`assistant.suggested.${q}`, q))}
                      className="w-full text-left text-xs px-3 py-2 rounded-xl border border-border hover:border-primary hover:bg-primary-light/30 transition-colors text-text-primary"
                      data-testid="assistant-suggested-question"
                    >
                      {tt(`assistant.suggested.${q}`, q)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-text-primary text-white rounded-br-md'
                      : 'bg-surface-muted text-text-primary rounded-bl-md'
                  }`}
                  data-testid={`assistant-msg-${m.role}`}
                >
                  {m.content || (sending && m.role === 'assistant' ? '…' : '')}
                </div>
              </div>
            ))}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage();
            }}
            className="border-t border-border p-3 flex items-center gap-2 flex-shrink-0"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={tt('assistant.placeholder', 'Type your question…')}
              disabled={sending}
              maxLength={2000}
              className="flex-1 px-3 py-2 text-sm rounded-full border border-border focus:outline-none focus:border-primary disabled:opacity-60"
              data-testid="assistant-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="bg-primary hover:bg-emerald-700 disabled:opacity-40 text-white p-2 rounded-full transition-colors"
              data-testid="assistant-send"
              aria-label={tt('assistant.sendLabel', 'Send')}
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default AssistantChat;
